import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

const KEYBOARD_INSET_VAR = "--keyboard-inset-bottom";

/**
 * Last measured keyboard height in px. Kept in a variable as well as on the
 * root element so the scroll logic can read it without a style recalc.
 */
let lastInset = 0;

/**
 * `window.innerHeight` sampled while nothing was covering the page. Used to tell
 * "the viewport shrank to make room for the keyboard" apart from "the keyboard
 * is floating over an unchanged viewport", which need opposite handling in
 * `getVisibleBottom`.
 */
let layoutBaseline = 0;

function setKeyboardInset(px: number): void {
  const root = document.documentElement;
  lastInset = px > 0 ? px : 0;
  if (px > 0) {
    root.style.setProperty(KEYBOARD_INSET_VAR, `${Math.round(px)}px`);
  } else {
    root.style.removeProperty(KEYBOARD_INSET_VAR);
    layoutBaseline = window.innerHeight;
  }
}

function measureVisualViewportKeyboard(): number {
  const vv = window.visualViewport;
  if (!vv) return 0;
  return Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
}

/**
 * Bottom edge of the region currently visible, in layout-viewport coordinates.
 * A focused field has to be scrolled above this line to be readable.
 *
 * Self-correcting about the three ways a keyboard can present itself, which
 * matters because it has to work both in mobile Safari and in the native
 * WebView. 2px tolerance throughout, because engines report sub-pixel
 * differences between the two heights when nothing is actually covering the
 * page:
 *
 * - The layout viewport shrank → it already excludes the keyboard, so the
 *   measured inset must *not* be subtracted on top of it or the keyboard gets
 *   counted twice. The visual viewport is the tighter of the two.
 * - The layout viewport is untouched but the visual viewport shrank → iOS
 *   Safari, which shrinks the visual viewport but not `innerHeight`. It is
 *   authoritative.
 * - Neither shrank → the keyboard is overlaying a full-height viewport, which
 *   is Capacitor with `resize: "body"`. The measured inset has to be subtracted.
 */
export function getVisibleBottom(): number {
  if (typeof window === "undefined") return 0;
  const layoutBottom = window.innerHeight;
  const vv = window.visualViewport;

  if (layoutBaseline > 0 && layoutBottom < layoutBaseline - 2) {
    return vv ? vv.offsetTop + vv.height : layoutBottom;
  }

  if (!vv) return layoutBottom - lastInset;
  const viewportBottom = vv.offsetTop + vv.height;
  if (viewportBottom < layoutBottom - 2) return viewportBottom;
  return layoutBottom - lastInset;
}

/** Keeps `--keyboard-inset-bottom` in sync for drawer scroll padding (native + mobile web). */
export function bindKeyboardInsetSync(): () => void {
  if (typeof window === "undefined") return () => {};

  layoutBaseline = window.innerHeight;

  const onVisualViewportChange = () => {
    if (Capacitor.isNativePlatform()) return;
    setKeyboardInset(measureVisualViewportKeyboard());
  };

  window.visualViewport?.addEventListener("resize", onVisualViewportChange);
  window.visualViewport?.addEventListener("scroll", onVisualViewportChange);

  const nativeRemovers: Array<() => void> = [];

  if (Capacitor.isPluginAvailable("Keyboard")) {
    void Keyboard.addListener("keyboardWillShow", (info) => {
      setKeyboardInset(info.keyboardHeight);
    }).then((handle) => {
      nativeRemovers.push(() => void handle.remove());
    });

    void Keyboard.addListener("keyboardWillHide", () => {
      setKeyboardInset(0);
    }).then((handle) => {
      nativeRemovers.push(() => void handle.remove());
    });
  }

  return () => {
    window.visualViewport?.removeEventListener("resize", onVisualViewportChange);
    window.visualViewport?.removeEventListener("scroll", onVisualViewportChange);
    for (const remove of nativeRemovers) remove();
    setKeyboardInset(0);
  };
}
